/**
 * Owner-only legal-hold client (legal-review follow-up 2026-10-25).
 *   GET  /v1/account/legal-hold   — read status.
 *   POST /v1/account/legal-hold   — turn on with a short note.
 *   DELETE /v1/account/legal-hold — turn off.
 */

import type { AccountLegalHoldStatus } from '@sill/types/api';

import type { HttpClient } from './http-client.js';
import { ApiError } from './http-client.js';

export type LegalHoldResult<T> =
  | { readonly kind: 'ok'; readonly value: T }
  | { readonly kind: 'error'; readonly message: string };

function explain(err: ApiError): string {
  if (err.status === 403) return 'Only the account owner can change the legal hold.';
  if (err.status === 400 && err.server_reason === 'reason_too_long') {
    return 'The reason you entered is too long. Please shorten it.';
  }
  return 'Something went wrong. Please try again.';
}

async function call<T>(fn: () => Promise<T>): Promise<LegalHoldResult<T>> {
  try {
    return { kind: 'ok', value: await fn() };
  } catch (err) {
    if (err instanceof ApiError) return { kind: 'error', message: explain(err) };
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : 'Network error.',
    };
  }
}

export function getLegalHoldStatus(
  http: HttpClient,
): Promise<LegalHoldResult<AccountLegalHoldStatus>> {
  return call(async () => {
    const res = await http.request<AccountLegalHoldStatus>({
      method: 'GET',
      path: '/v1/account/legal-hold',
    });
    if (res === undefined) throw new Error('Empty response body.');
    return res;
  });
}

export function turnOnLegalHold(
  http: HttpClient,
  reason: string,
): Promise<LegalHoldResult<AccountLegalHoldStatus>> {
  return call(async () => {
    const res = await http.request<AccountLegalHoldStatus>({
      method: 'POST',
      path: '/v1/account/legal-hold',
      body: { reason },
    });
    if (res === undefined) throw new Error('Empty response body.');
    return res;
  });
}

export function turnOffLegalHold(
  http: HttpClient,
): Promise<LegalHoldResult<AccountLegalHoldStatus>> {
  return call(async () => {
    const res = await http.request<AccountLegalHoldStatus>({
      method: 'DELETE',
      path: '/v1/account/legal-hold',
    });
    if (res === undefined) throw new Error('Empty response body.');
    return res;
  });
}
